<template>
  <div>
    <Highcharts v-if="hasData" :options="chartOptions"></Highcharts>
    <v-empty-state
      v-else
      icon="mdi-chart-multiple"
      title="Aucune donnée à comparer"
      text="Choisissez une station dans un calcul. Les stations sans historique récent ne donnent pas de courbe."
      size="small"
    ></v-empty-state>
  </div>
</template>

<script>
  import { Chart } from "highcharts-vue";
  // Site options (French dates, theme of the site rather than of the device)
  import "@/plugins/highcharts";
  import { COMPARISON_COLORS, getAccuracyLabel, parseReadingTime } from "@/utils/flowFormulas";
  import { ZONE_BAND_COLORS } from "@/utils/zoneColors";

  // Station tab of the section editor: every formula of the section on one chart, so the admins can compare them
  // before choosing the primary one. The primary formula is the thick line, the others are dashed.
  export default {
    name: "FormulaComparisonChart",
    components: { Highcharts: Chart },
    props: {
      // [{ id, title, accuracy, primary, level: [{ date, hour, flow }] }], level newest first
      results: {
        type: Array,
        default: () => [],
      },
      minSugLevel: {
        type: Number,
        default: NaN,
      },
      maxSugLevel: {
        type: Number,
        default: NaN,
      },
    },
    computed: {
      isDark() {
        return Boolean(this.$vuetify.theme.current && this.$vuetify.theme.current.dark);
      },
      series() {
        // Marine (Écume on the dark theme) for the primary formula
        const primaryColor = this.isDark ? "#8FD3E0" : "#0B3C5D";
        return this.results
          .map((result, index) => ({
            name: `${result.title} · ${getAccuracyLabel(result.accuracy)}${result.primary ? " · principal" : ""}`,
            type: "line",
            color: result.primary ? primaryColor : COMPARISON_COLORS[index % COMPARISON_COLORS.length],
            lineWidth: result.primary ? 3 : 2,
            dashStyle: result.primary ? "Solid" : "ShortDash",
            zIndex: result.primary ? 2 : 1,
            marker: { enabled: false },
            data: (result.level || [])
              .map((entry) => [parseReadingTime(entry.date, entry.hour), Number(entry.flow)])
              .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y))
              .sort((a, b) => a[0] - b[0]),
          }))
          .filter((series) => series.data.length);
      },
      hasData() {
        return this.series.length > 0;
      },
      chartOptions() {
        const min = this.minSugLevel;
        const max = this.maxSugLevel;
        const hasRange = Number.isFinite(min) && Number.isFinite(max) && max > min;
        return {
          chart: { height: 380, spacing: [8, 8, 8, 8] },
          title: { text: null },
          credits: { enabled: false },
          exporting: { enabled: false },
          legend: { enabled: true },
          xAxis: {
            type: "datetime",
            crosshair: true,
            dateTimeLabelFormats: { minute: "%H:%M", hour: "%H:%M", day: "%e %b" },
          },
          yAxis: {
            title: { text: null },
            min: 0,
            labels: { format: "{value} mcs" },
            plotBands: hasRange ? [{ from: min, to: max, color: ZONE_BAND_COLORS.recommended }] : [],
          },
          tooltip: {
            shared: true,
            xDateFormat: "%e %b %H:%M",
            valueSuffix: " mcs",
            valueDecimals: 2,
          },
          series: this.series,
        };
      },
    },
  };
</script>
